import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const AUDIENCES = [
    'Corporates', 'CSR teams', 'HR leaders', 'Event & wedding planners',
    'Hotels & hospitality', 'Universities', 'Professionals', 'Entrepreneurs',
    'Students', 'Families', 'Conscious travellers', 'Philanthropists', 'Communities',
];

const PRINCIPLES = [
    { n: '01', t: 'Community-first design', b: 'Every program is co-created with the community it serves. No parachuting in.' },
    { n: '02', t: 'Dignified livelihoods', b: 'Fair pay, fair credit, fair recognition. Artisans as authors, not vendors.' },
    { n: '03', t: 'Regenerative by default', b: 'We measure not just what we do, but what we leave behind — for the land, for the culture.' },
    { n: '04', t: 'Radical transparency', b: 'Public ledgers of impact. Where every rupee goes, and what it grows into.' },
];

export default function About() {
    return (
        <div data-testid="about-page">
            {/* Hero */}
            <section className="relative pt-40 pb-24 md:pt-56 md:pb-32 bg-aurika-linen border-b border-aurika-border">
                <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-8">
                        <p className="overline">About Aurika</p>
                        <h1 className="mt-6 font-display font-light text-aurika-obsidian leading-[0.98] tracking-[-0.02em]
                            text-[3rem] sm:text-[4.25rem] md:text-[5.5rem] lg:text-[6.5rem] text-balance">
                            A quiet <em className="italic text-aurika-terracotta">rebellion</em> against the way impact is usually done.
                        </h1>
                    </div>
                    <div className="lg:col-span-4 lg:pt-10 text-aurika-ink font-light text-lg leading-relaxed">
                        <p>
                            Aurika Collective Foundation is a social enterprise and impact partner — designing experiences,
                            partnerships and campaigns that create meaningful social change while helping people become
                            active participants in rural transformation.
                        </p>
                    </div>
                </div>
            </section>

            {/* Mission & Vision */}
            <section className="section-y bg-aurika-bone">
                <div className="container-editorial grid grid-cols-1 md:grid-cols-2 gap-16">
                    <div className="md:pr-10">
                        <p className="overline">Mission</p>
                        <p className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl font-light text-aurika-obsidian leading-[1.05]">
                            Building a world where <em className="italic text-aurika-terracotta">everyday experiences</em> create meaningful impact.
                        </p>
                    </div>
                    <div className="md:pl-10 md:border-l border-aurika-border">
                        <p className="overline">Vision</p>
                        <p className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl font-light text-aurika-obsidian leading-[1.05]">
                            India’s most <em className="italic text-aurika-terracotta">trusted platform</em> for creating sustainable rural prosperity.
                        </p>
                    </div>
                </div>
            </section>

            {/* 3E Detail */}
            <section className="section-y bg-aurika-linen">
                <div className="container-editorial">
                    <p className="overline">The 3E Framework</p>
                    <h2 className="mt-4 font-display text-5xl md:text-6xl font-light text-aurika-obsidian leading-[1]">
                        Three commitments. <em className="italic">One compass.</em>
                    </h2>

                    <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-10">
                        {[
                            { l: 'Empowerment', b: 'Through education, skill development, entrepreneurship, mentoring and capacity building.' },
                            { l: 'Employment', b: 'Sustainable livelihood opportunities for artisans, farmers, women, youth and rural entrepreneurs.' },
                            { l: 'Environmental Sustainability', b: 'Circular economy practices, conscious consumption, sustainable sourcing and regenerative tourism.' },
                        ].map((e, i) => (
                            <div key={e.l} data-testid={`about-3e-${i}`} className="border-t border-aurika-obsidian pt-8">
                                <div className="flex items-baseline gap-3">
                                    <span className="font-display text-[10rem] leading-none text-aurika-terracotta">E</span>
                                </div>
                                <h3 className="mt-4 font-display text-3xl font-normal text-aurika-obsidian">{e.l}</h3>
                                <p className="mt-3 text-aurika-ink font-light">{e.b}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Principles */}
            <section className="section-y bg-aurika-bone">
                <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <p className="overline">How We Work</p>
                        <h2 className="mt-4 font-display text-5xl md:text-6xl font-light text-aurika-obsidian leading-[1]">
                            Four <em className="italic">principles</em> we don’t break.
                        </h2>
                    </div>
                    <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-12">
                        {PRINCIPLES.map((p) => (
                            <div key={p.n}>
                                <span className="font-mono-brand text-xs tracking-widest text-aurika-terracotta">{p.n}</span>
                                <h3 className="mt-3 font-display text-2xl md:text-3xl font-normal text-aurika-obsidian">{p.t}</h3>
                                <p className="mt-3 text-aurika-ink font-light">{p.b}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Who we serve */}
            <section className="section-y bg-aurika-obsidian text-aurika-bone">
                <div className="container-editorial">
                    <p className="overline text-aurika-ochre">Who Walks With Us</p>
                    <h2 className="mt-4 font-display text-5xl md:text-6xl font-light leading-[1]">
                        A collective of the <em className="italic text-aurika-ochre">curious and the committed.</em>
                    </h2>
                    <div className="mt-16 flex flex-wrap gap-3">
                        {AUDIENCES.map((a) => (
                            <span key={a} className="px-5 py-2.5 border border-aurika-bone/25 text-sm tracking-wide hover:bg-aurika-terracotta hover:border-aurika-terracotta transition-colors duration-300">
                                {a}
                            </span>
                        ))}
                    </div>

                    <div className="mt-16">
                        <Link to="/partner" data-testid="about-partner-cta" className="btn-ghost-light">
                            Walk with us <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
